import { ApiQueryParams, DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { EntityType, useAppSelector } from '@frontend/common';
import { ErrorHandler } from '@frontend/error-handler';
import { FunctionalityClient } from '@frontend/functionality/api';
import { Feature, FeatureTarget, Functionality } from '@frontend/functionality/types';
import { UserGroupClient } from '@frontend/group/api';
import { IGroup } from '@frontend/group/types';
import { formStore, updateField, useAccountRepository } from '@frontend/repository';
import { SettingClient } from '@frontend/setting/api';
import { SettingKey } from '@frontend/setting/types';
import { ToastUtil } from '@frontend/toast-utils';
import { UserContactClient } from '@frontend/user/api';
import { IUser } from '@frontend/user/types';
import { User } from '@frontend/user/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../../../redux/store';
import { CreateTransactionModalProps } from './create-transaction-modal.component';

export type CreateTransactionReceiverOption = (IUser | IGroup) & { receiverType: string };

export function dedupeUsersById(users: IUser[]): IUser[] {
    const seen = new Set<string>();
    const unique: IUser[] = [];
    users.forEach((user) => {
        if (seen.has(user.id)) {
            return;
        }
        seen.add(user.id);
        unique.push(user);
    });
    return unique;
}

export function formatCreateTransactionPartyLabel(obj: IUser | IGroup): string {
    if ('first_name' in obj) {
        return new User(obj).getDisplayName();
    }
    return obj.name;
}

export async function fetchCreateTransactionContacts(
    arg: ApiQueryParams<DefaultQueryParams> | undefined,
    accountId: string
): Promise<PaginatedResponse<IUser>> {
    const [personal, corporate] = await Promise.all([
        UserContactClient.getMyContacts({ ...arg, account_id: accountId }),
        UserContactClient.getCorporateContacts({ ...arg, account_id: accountId })
    ]);
    const results = dedupeUsersById([...personal.results, ...corporate.results]);

    return {
        count: personal.count + corporate.count,
        results
    };
}

export async function fetchCreateTransactionReceivers(
    arg: ApiQueryParams<DefaultQueryParams> | undefined,
    accountId: string,
    labels: { users: string; userGroups: string }
): Promise<PaginatedResponse<CreateTransactionReceiverOption>> {
    const [contacts, userGroups] = await Promise.all([
        fetchCreateTransactionContacts(arg, accountId),
        UserGroupClient.fetchUserGroups({ ...arg, account_id: accountId })
    ]);

    const usersWithType = contacts.results.map((user) => ({
        ...user,
        receiverType: labels.users
    }));
    const groupsWithType = userGroups.results.map((group) => ({
        ...group,
        receiverType: labels.userGroups
    }));

    return {
        count: contacts.count + userGroups.count,
        results: [...usersWithType, ...groupsWithType]
    };
}

export const useCreateTransactionModal = (props: CreateTransactionModalProps) => {
    const { currentAccount } = useAccountRepository();
    const dispatch = useAppDispatch();
    const formState = useAppSelector(useSelector, formStore);
    const form = props.formId ? (formState[props.formId] ? formState[props.formId] : null) : null;
    const [functionalities, setFunctionalities] = useState<Functionality[]>([]);
    const [selectedFunctionality, setSelectedFunctionality] = useState<Functionality | null>(null);

    const [userGroupMembers, setUserGroupMembers] = useState<Map<string, string[]>>(new Map());
    const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
    const [hasColliWorkflow, setHasColliWorkflow] = useState(false);
    const previousSelectionRef = useRef<{ userIds: string[]; groupIds: string[] }>({ userIds: [], groupIds: [] });
    const groupMemberCache = useRef<Map<string, string[]>>(new Map());

    useEffect(() => {
        FunctionalityClient.fetchFunctionalities({
            account_id: currentAccount,
            target: FeatureTarget.PLATFORM,
            application: props.application,
            feature: props.feature ? [props.feature] : [Feature.INBOUND_TRANSACTION, Feature.OUTBOUND_TRANSACTION, Feature.TRANSACTIONS]
        }).then((res) => {
            setFunctionalities(res.results);
            if (res.results.length === 1) {
                setSelectedFunctionality(res.results[0]);
            }
        });
    }, [currentAccount]);

    useEffect(() => {
        if (!selectedFunctionality) {
            setHasColliWorkflow(false);
            return;
        }
        SettingClient.fetchSettings({
            entity_type: EntityType.FUNCTIONALITY,
            entity_id: selectedFunctionality.id,
            account_id: selectedFunctionality.account_id
        })
            .then((res) => {
                if (res.results.length === 0) {
                    ToastUtil.warning('No settings found for the selected functionality.');
                    setHasColliWorkflow(false);
                    return;
                } else {
                    const colliWorkflow = res.results.find((setting) => setting.key === SettingKey.COLLI_WORKFLOW_ID)?.value;
                    setHasColliWorkflow(typeof colliWorkflow === 'string' && colliWorkflow.length > 0);
                    res.results
                        .filter((setting) => setting.key !== SettingKey.DEFAULT_SPOT_ID)
                        .forEach((setting) => {
                            if (!props.formId) return;
                            dispatch(
                                updateField({
                                    formId: props.formId,
                                    key: mapSetingKey(setting.key),
                                    value: setting.value
                                })
                            );
                        });
                }
            })
            .catch((err) => ErrorHandler.handleError({ error: err }));
    }, [selectedFunctionality]);

    function mapSetingKey(key: string): string {
        switch (key) {
            case SettingKey.TRANSACTION_WORKFLOW_ID:
                return 'workflow_id';
            default:
                return key;
        }
    }

    const handleReceiverChange = useCallback(
        async (selectedUsers: IUser[], selectedGroups: IGroup[]) => {
            if (!props.formId) return;

            const newGroupIds = selectedGroups.map((g) => g.id);
            const newUserIds = selectedUsers.map((u) => u.id);

            const addedGroupIds = newGroupIds.filter((id) => !previousSelectionRef.current.groupIds.includes(id));
            const removedGroupIds = previousSelectionRef.current.groupIds.filter((id) => !newGroupIds.includes(id));

            const updatedMap = new Map(userGroupMembers);
            removedGroupIds.forEach((groupId) => {
                updatedMap.delete(groupId);
            });

            const fetchPromises = addedGroupIds.map(async (groupId) => {
                // Check cache first
                if (groupMemberCache.current.has(groupId)) {
                    return { groupId, userIds: groupMemberCache.current.get(groupId)! };
                }
                const members = await UserGroupClient.fetchUserGroupMembers({ group_id: groupId });
                const userIds = members.results.map((m) => m.entity_id);
                groupMemberCache.current.set(groupId, userIds);
                return { groupId, userIds };
            });

            const fetchedGroups = await Promise.all(fetchPromises);
            fetchedGroups.forEach(({ groupId, userIds }) => {
                updatedMap.set(groupId, userIds);
            });

            setUserGroupMembers(updatedMap);
            setSelectedGroupIds(newGroupIds);

            dispatch(updateField({ formId: props.formId, key: 'receivers', value: newUserIds }));

            previousSelectionRef.current = { userIds: newUserIds, groupIds: newGroupIds };
        },
        [props.formId, userGroupMembers, dispatch]
    );

    const getAllReceiverUserIds = useCallback((): string[] => {
        const directUserIds = form?.value?.receivers ?? [];
        const groupUserIds = Array.from(userGroupMembers.values()).flat();
        return [...new Set([...directUserIds, ...groupUserIds])];
    }, [form?.value?.receivers, userGroupMembers]);

    return {
        functionalities,
        currentAccount,
        selectedFunctionality,
        setSelectedFunctionality,
        form,
        dispatch,
        handleReceiverChange,
        getAllReceiverUserIds,
        selectedGroupIds,
        hasColliWorkflow
    };
};
